import { type ZodTypeAny } from "zod";

const getSchemaType = (schema: ZodTypeAny | undefined): string | undefined => (schema as any)?._def?.type;

const getInnerSchema = (schema: ZodTypeAny): ZodTypeAny | undefined => (schema as any)._def?.innerType;

const isNumericPathPart = (part: string) => /^\d+$/.test(part);

const isOptionalSchema = (schema: ZodTypeAny | undefined): boolean => {
  if (!schema) return true;
  if (typeof (schema as any).isOptional === "function" && (schema as any).isOptional()) return true;

  const schemaType = getSchemaType(schema);
  if (schemaType === "optional" || schemaType === "default" || schemaType === "catch") return true;

  return false;
};

const unwrapSchema = (schema: ZodTypeAny | undefined): ZodTypeAny | undefined => {
  let current = schema;

  while (current) {
    const schemaType = getSchemaType(current);
    if (schemaType !== "optional" && schemaType !== "nullable" && schemaType !== "default" && schemaType !== "catch") {
      return current;
    }

    current = getInnerSchema(current);
  }

  return current;
};

const getObjectShape = (schema: ZodTypeAny): Record<string, ZodTypeAny> | undefined => {
  const shape = (schema as any).shape ?? (schema as any)._def?.shape;
  return typeof shape === "function" ? shape() : shape;
};

const getArrayElementSchema = (schema: ZodTypeAny): ZodTypeAny | undefined => {
  return (schema as any).element ?? (schema as any)._def?.element ?? (schema as any)._def?.type;
};

export const isFieldRequiredBySchema = (schema: ZodTypeAny | undefined, name: string): boolean => {
  if (!schema || !name) return false;

  const pathParts = name.split(".").filter(Boolean);
  if (pathParts.length === 0) return false;

  let current: ZodTypeAny | undefined = schema;

  for (const [index, pathPart] of pathParts.entries()) {
    if (isOptionalSchema(current)) return false;

    current = unwrapSchema(current);
    if (!current) return false;

    const schemaType = getSchemaType(current);

    if (schemaType === "object") {
      const shape = getObjectShape(current);
      current = shape?.[pathPart];
    } else if (schemaType === "array" && isNumericPathPart(pathPart)) {
      current = getArrayElementSchema(current);
    } else {
      return false;
    }

    if (!current) return false;

    if (index === pathParts.length - 1) {
      return !isOptionalSchema(current);
    }
  }

  return false;
};
